import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, MessageSquarePlus, X, Send, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import useAnalytics from '../hooks/useAnalytics';

// Enhanced Helpful Feedback Widget with Thumbs Up/Down
export const HelpfulFeedback = ({ reportId, venueName }) => {
  const [feedbackGiven, setFeedbackGiven] = useState(null); // 'helpful', 'not-helpful', or null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { trackHelpfulFeedback } = useAnalytics();

  const handleFeedback = async (isHelpful) => {
    if (feedbackGiven || isSubmitting) return;
    
    setIsSubmitting(true);
    const success = await trackHelpfulFeedback(isHelpful, reportId, venueName);
    
    if (success) {
      setFeedbackGiven(isHelpful ? 'helpful' : 'not-helpful');
      toast.success('Thanks for your feedback!');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="flex flex-col items-center justify-center p-6 bg-stone-50 rounded-xl border border-stone-200">
      <span className="text-sm text-stone-600 mb-4">Did you find this report helpful?</span>
      <div className="flex items-center gap-3">
        {/* Thumbs Up */}
        <Button
          variant={feedbackGiven === 'helpful' ? "default" : "outline"}
          size="sm"
          onClick={() => handleFeedback(true)}
          disabled={feedbackGiven !== null || isSubmitting}
          className={
            feedbackGiven === 'helpful' 
              ? "bg-green-600 hover:bg-green-600 text-white" 
              : feedbackGiven === 'not-helpful'
                ? "opacity-50"
                : "hover:bg-green-50 hover:border-green-400 hover:text-green-700"
          }
          data-testid="helpful-yes-btn"
        >
          {feedbackGiven === 'helpful' ? (
            <>
              <Check className="w-4 h-4 mr-1" />
              Helpful
            </>
          ) : (
            <>
              <ThumbsUp className="w-4 h-4 mr-1" />
              Yes
            </>
          )}
        </Button>

        {/* Thumbs Down */}
        <Button
          variant={feedbackGiven === 'not-helpful' ? "default" : "outline"}
          size="sm"
          onClick={() => handleFeedback(false)}
          disabled={feedbackGiven !== null || isSubmitting}
          className={
            feedbackGiven === 'not-helpful' 
              ? "bg-stone-600 hover:bg-stone-600 text-white" 
              : feedbackGiven === 'helpful'
                ? "opacity-50"
                : "hover:bg-stone-100 hover:border-stone-400"
          }
          data-testid="helpful-no-btn"
        >
          {feedbackGiven === 'not-helpful' ? (
            <>
              <Check className="w-4 h-4 mr-1" />
              Noted
            </>
          ) : (
            <>
              <ThumbsDown className="w-4 h-4 mr-1" />
              No
            </>
          )}
        </Button>
      </div>
      
      {feedbackGiven && (
        <p className="text-xs text-stone-400 mt-3 animate-fade-in">
          Your feedback helps us improve our reports
        </p>
      )}
    </div>
  );
};

// Feature Request Form
export const FeatureFeedbackButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [featureText, setFeatureText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { submitFeatureRequest } = useAnalytics();
  const location = useLocation();
  const isVenueStoryPage = /^\/venues\/[^/]+/.test(location.pathname);
  if (isVenueStoryPage) return null;

  const handleSubmit = async () => {
    if (!featureText.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    const success = await submitFeatureRequest(featureText.trim());
    
    if (success) {
      setSubmitted(true);
      setFeatureText('');
      toast.success('Thank you for your suggestion!');
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
      }, 2000);
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 bg-white border border-stone-200 rounded-full shadow-lg hover:shadow-xl hover:border-stone-300 transition-all text-sm text-stone-600 hover:text-stone-900"
        data-testid="feature-feedback-btn"
      >
        <MessageSquarePlus className="w-4 h-4" />
        <span className="hidden sm:inline">Suggest a feature</span>
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Share Your Ideas</DialogTitle>
          </DialogHeader>
          
          {submitted ? (
            <div className="py-8 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-stone-600">Thank you for helping us improve!</p>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-stone-500">
                What else would you like us to add to improve this platform?
              </p>
              <Textarea
                value={featureText}
                onChange={(e) => setFeatureText(e.target.value)}
                placeholder="Share your suggestion..."
                className="min-h-[120px] resize-none"
                maxLength={500}
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-stone-400">{featureText.length}/500</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!featureText.trim() || isSubmitting}
                    className="bg-stone-900 hover:bg-stone-800"
                  >
                    {isSubmitting ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default { HelpfulFeedback, FeatureFeedbackButton };
